import { formatMoney, unitDivisor } from './currency';
import { types as sdkTypes } from './sdkLoader';

const { Money } = sdkTypes;

/**
 * Display helpers for listing fields defined in Console.
 *
 * Console stores enum values (e.g. "the-vampires-wife") separately from their
 * labels ("The Vampire's Wife"). Values are immutable once created, so any
 * label fixing happens here at display time — never by renaming in Console.
 *
 * Shared by ListingCard and ListingPage so the two present a listing identically.
 */

// Exactly one of these applies to a listing — Console's category limits decide
// which. First match wins.
export const SIZE_FIELD_KEYS = ['size_clothing', 'size_shoes', 'size_suits'];

const findFieldConfig = (listingFields, key) => listingFields?.find(f => f.key === key);

/**
 * Resolves an enum value to its Console label. Options are compared as strings,
 * matching fieldHelpers, since numeric options arrive as numbers.
 *
 * @param {Array} listingFields hosted listing field configs
 * @param {string} key field key, e.g. "designer"
 * @param {*} value the stored enum value
 * @returns {string|null} the label, or null when unresolvable
 */
export const getEnumLabel = (listingFields, key, value) => {
  if (value == null) {
    return null;
  }
  const fieldConfig = findFieldConfig(listingFields, key);
  const selected = fieldConfig?.enumOptions?.find(o => `${value}` === `${o.option}`);
  return selected?.label || null;
};

/**
 * Resolves a multi-enum value array to its Console labels, dropping any that
 * no longer exist in the config.
 */
export const getMultiEnumLabels = (listingFields, key, values) => {
  if (!Array.isArray(values) || values.length === 0) {
    return [];
  }
  const fieldConfig = findFieldConfig(listingFields, key);
  return values
    .map(value => fieldConfig?.enumOptions?.find(o => `${value}` === `${o.option}`)?.label)
    .filter(Boolean);
};

/**
 * The one size label that applies to this listing, whichever size field it uses.
 */
export const getSizeLabel = (listingFields, publicData) =>
  SIZE_FIELD_KEYS.reduce(
    (found, key) => found || getEnumLabel(listingFields, key, publicData?.[key]),
    null
  );

/**
 * The designer name, falling back to the free-text `designer_other` when the
 * lender picked "Other".
 */
export const getDesignerLabel = (listingFields, publicData) =>
  getEnumLabel(listingFields, 'designer', publicData?.designer) ||
  publicData?.designer_other ||
  null;

/**
 * Formats a listing field that stores a price in WHOLE currency units.
 *
 * Console's number fields (retail_value, purchase_price) hold whole units —
 * 495 means $495 — whereas Money expects subunits. Returns null for unset,
 * zero, negative or unsupported-currency values so callers can simply omit the
 * line rather than render a broken one.
 *
 * @param {number} wholeUnits e.g. 495
 * @param {string} currency marketplace currency code
 * @param {Object} intl React Intl instance
 * @returns {string|null} formatted price
 */
export const formatWholeUnitPrice = (wholeUnits, currency, intl) => {
  const isUsable = typeof wholeUnits === 'number' && wholeUnits > 0;
  if (!isUsable) {
    return null;
  }
  try {
    return formatMoney(intl, new Money(Math.round(wholeUnits * unitDivisor(currency)), currency));
  } catch (e) {
    // Unsupported currency — skip the line rather than break the page.
    return null;
  }
};
